import { CoreLayoutView, TCoreOptions, useThemeHook } from '@core/widgets';
import { eventBus } from '@libs/core';
import { SystemEventTypeEnum, TCommonData, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import Dash from '../../common/Dash';
import { useDetectKeyboard } from '../../hooks/useDetectKeyboard';
import { getSemanticColors, SemanticColor } from '../../theme/colors';
import { LayoutEnum } from '../../types/enums';
import { TLayoutOption } from '../../types/options';
import { isMobileByTouch } from '../../utils/helpers';
import { getInlineStyles } from '../../utils/styles';
import { getBackgroundHref, resolveCssColor } from '../Image/imageUtils';
import styles from './css/separateLayout.module.css';
import { clearScrollPosition, getScrollKey, getScrollPosition, saveScrollPosition } from './separateLayoutScroll';
import { SeparateLayoutSheetContext, useSheetFocusSync } from './separateLayoutSheet';

type TSeparateLayoutProps = {
    topWidget: React.ReactNode;
    bottomWidget: React.ReactNode;
} & TWidgetProps<TCommonData, TLayoutOption>;

type TSeparateContentLayoutPalette = {
    statusBarColor?: string;
    expandedShadowColor?: string;
    expandedHeight?: number;
    backgroundColor?: string;
} & TCoreOptions;

const BOTTOM_MOVE = 'bottom-move';
/** How long a restored scroll position is re-applied while the sheet content is not tall enough yet. */
const RESTORE_TIMEOUT_MS = 1000;
/** User input that cancels a pending scroll restore. */
const RESTORE_CANCEL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

const SeparateContentLayout = ({ widget, topWidget, bottomWidget }: TSeparateLayoutProps) => {
    const { isKeyboardOpen, setIsKeyboardOpen } = useDetectKeyboard();
    const scrollValueForClose = isMobileByTouch() ? 0.5 : 0;
    const scrollStep = isMobileByTouch() ? 0.7 : 0.5;
    // Only SeparateContentLayout (not CalculatorContentLayout) remembers its open sheet within the SPA session.
    const storageKey = widget.id === LayoutEnum.SEPARATE_CONTENT_LAYOUT && widget.internalId ? getScrollKey(widget.internalId) : undefined;
    const [restoredScroll] = useState(() => (storageKey ? getScrollPosition(storageKey) : undefined));
    const isRestored = restoredScroll !== undefined;
    const [classBottom, setClassBottom] = useState(() => (isRestored ? BOTTOM_MOVE : ''));
    const [openedBottomLayout, setOpenedBottomLayout] = useState(() => isRestored);
    const [overlayShouldBeClosed, setOverlayShouldBeClosed] = useState(() => restoredScroll !== undefined && restoredScroll <= scrollStep);
    const [topPosition, setTopPosition] = useState<number>();
    // animationInProgress: 0 - no animation, 1 - opening, -1 - closing
    const [animationInProgress, setAnimationInProgress] = useState(0);
    const ref = useRef<HTMLDivElement | null>(null);
    const refStartAnimation = useRef(false);
    const isUserAction = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The scroll position still to be applied to the restored sheet; `undefined` once applied or cancelled.
    const restoreTargetRef = useRef(restoredScroll);
    // Swallows the scroll event caused by applying the restored position, so it is not taken for user input.
    const skipNextScrollRef = useRef(false);
    // The keyboard is closed on mount: keeps the keyboard sync from collapsing the restored sheet until the keyboard is opened.
    const restoredKeyboardGuardRef = useRef(isRestored);
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];
    const theme = useThemeHook<TSeparateContentLayoutPalette>(widget);

    const backgroundImage = widget.options?.backgroundImage;
    const backgroundColor = widget.options?.backgroundColor;
    const version = widget.version;

    const EXPANDED_HEIGHT = theme.palette?.expandedHeight ?? 65;
    const EXPANDED_HEIGHT_FOR_INNER = EXPANDED_HEIGHT - 5;

    useLayoutEffect(() => {
        const resolvedBackgroundColor = resolveCssColor(backgroundColor, version);
        if (resolvedBackgroundColor) {
            document.body.style.backgroundColor = resolvedBackgroundColor;
        } else {
            document.body.style.backgroundColor = getSemanticColors(version)[SemanticColor.BgSeparateContentLayoutTop];
        }
        if (backgroundImage) {
            document.body.style.backgroundImage = `url(${getBackgroundHref(backgroundImage)})`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundPosition = 'center top';
        }
        const upContainer = document?.getElementById('top-container');
        const rect = upContainer?.getBoundingClientRect();
        const bottomLayoutPosition = rect?.bottom === 0 ? EXPANDED_HEIGHT : rect?.bottom;

        requestAnimationFrame(() => {
            setTopPosition(bottomLayoutPosition);
        });

        return () => {
            document.body.style.backgroundColor = '';
            document.body.style.backgroundImage = '';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundPosition = '';
        };
    }, [EXPANDED_HEIGHT, backgroundColor, backgroundImage, version]);

    const openBottomLayout = useCallback(() => {
        refStartAnimation.current = true;
        if (animationInProgress === 0) {
            setOpenedBottomLayout(true);
            setOverlayShouldBeClosed(true);
        }
        setAnimationInProgress(1);
        setClassBottom(BOTTOM_MOVE);
        window.scrollTo(0, 0);
        eventBus.emit(SystemEventTypeEnum.SIZE, widget.internalId, 'fullScreen');
    }, [animationInProgress, widget.internalId]);

    const openOnFocus = () => {
        if (openedBottomLayout || refStartAnimation.current) {
            return false;
        }
        // Like a user action: the sheet must not be collapsed back before the keyboard is detected.
        isUserAction.current = true;
        openBottomLayout();
        return true;
    };

    const { sheet, focusTarget, focusTargetOnTouchEnd, onTouchStart, onTouchEnd, onTouchCancel, blurFocused } = useSheetFocusSync(ref, openOnFocus);

    const closeBottomLayout = useCallback(() => {
        refStartAnimation.current = true;
        blurFocused();
        setClassBottom('');
        setAnimationInProgress(-1);
        setOpenedBottomLayout(false);
        if (ref.current) {
            ref.current.scrollTop = 0;
        }
        setIsKeyboardOpen(false);
        // Stops a pending restore: its next frame sees no target and bails out.
        restoreTargetRef.current = undefined;
        restoredKeyboardGuardRef.current = false;
        if (storageKey) {
            clearScrollPosition(storageKey);
        }
        eventBus.emit(SystemEventTypeEnum.SIZE, widget.internalId, 'notFullScreen');
    }, [blurFocused, setIsKeyboardOpen, storageKey, widget.internalId]);

    const emitRestoredSize = useEffectEvent(() => {
        if (isRestored) {
            eventBus.emit(SystemEventTypeEnum.SIZE, widget.internalId, 'fullScreen');
        }
    });

    useEffect(() => {
        emitRestoredSize();
    }, []);

    useEffect(() => {
        const element = ref.current;
        const target = restoreTargetRef.current;
        if (topPosition === undefined || target === undefined || !element) {
            return;
        }
        const start = performance.now();
        let frame = 0;

        const cancel = () => {
            cancelAnimationFrame(frame);
            restoreTargetRef.current = undefined;
            skipNextScrollRef.current = false;
            removeListeners();
        };
        const removeListeners = () => {
            RESTORE_CANCEL_EVENTS.forEach((type) => element.removeEventListener(type, cancel));
        };
        const finish = () => {
            restoreTargetRef.current = undefined;
            removeListeners();
            // The guard is set only when the position changed; drop it if its scroll event never came.
            frame = requestAnimationFrame(() => {
                skipNextScrollRef.current = false;
            });
        };
        const attempt = () => {
            // closeBottomLayout() (overlay click, keyboard hide) drops the target: stop without touching the collapsed sheet.
            if (restoreTargetRef.current === undefined) {
                skipNextScrollRef.current = false;
                removeListeners();
                return;
            }
            const before = element.scrollTop;
            element.scrollTop = target;
            if (element.scrollTop !== before) {
                skipNextScrollRef.current = true;
            }
            // The browser clamps scrollTop to the content height: retry while the content is still loading.
            if (Math.abs(element.scrollTop - target) < 1 || performance.now() - start > RESTORE_TIMEOUT_MS) {
                finish();
            } else {
                frame = requestAnimationFrame(attempt);
            }
        };

        RESTORE_CANCEL_EVENTS.forEach((type) => element.addEventListener(type, cancel, { passive: true }));
        attempt();

        return () => {
            cancelAnimationFrame(frame);
            removeListeners();
        };
    }, [topPosition]);

    const saveScroll = () => {
        if (storageKey && ref.current) {
            saveScrollPosition(storageKey, ref.current.scrollTop);
        }
    };

    const resetScroll = () => {
        if (ref.current) {
            ref.current.scrollTop = scrollStep;
        }
    };

    const onScroll = (_e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (skipNextScrollRef.current) {
            skipNextScrollRef.current = false;
            return;
        }
        if (!refStartAnimation.current) {
            isUserAction.current = true;
            if (!openedBottomLayout) {
                openBottomLayout();
                focusTargetOnTouchEnd();
                return;
            }
            if (ref?.current && ref.current?.scrollTop <= scrollValueForClose) {
                if (overlayShouldBeClosed || ref.current?.scrollTop === 0) {
                    closeBottomLayout();
                    return;
                }
                resetScroll();
                setOverlayShouldBeClosed(true);
            } else {
                if (ref.current && ref.current.scrollTop > scrollStep) {
                    setOverlayShouldBeClosed(false);
                }
            }
            saveScroll();
        }
    };

    const onDashTouch = () => {
        isUserAction.current = true;
        if (openedBottomLayout) {
            closeBottomLayout();
        } else {
            openBottomLayout();
            focusTarget();
        }
    };

    const handleClickOutside = () => {
        if (animationInProgress === 0) {
            closeBottomLayout();
        }
    };

    const onKeyboardToggle = useEffectEvent(() => {
        if (isKeyboardOpen) {
            restoredKeyboardGuardRef.current = false;
        }
        if (!isUserAction.current) {
            if (isKeyboardOpen && !openedBottomLayout) {
                openBottomLayout();
            } else if (!isKeyboardOpen && openedBottomLayout && !restoredKeyboardGuardRef.current) {
                closeBottomLayout();
            }
        }
    });

    useEffect(() => {
        onKeyboardToggle();
    }, [isKeyboardOpen, openedBottomLayout]);

    const classNamesBottomInner = clsx(
        styles.separateLayoutBottomInner,
        animationInProgress !== 0 && openedBottomLayout
            ? styles.animationExpandCollapseLayout
            : animationInProgress !== 0 && !openedBottomLayout
              ? styles.animationCollapseExpandLayout
              : '',
    );
    const styleBottom = {
        '--bottom-top': (classBottom === BOTTOM_MOVE ? EXPANDED_HEIGHT : topPosition) + 'px',
        '--bottom-overscroll-behavior': classBottom === BOTTOM_MOVE ? 'contain' : 'auto',
        '--bottom-overflowY': animationInProgress === 0 ? 'auto' : 'hidden',
    } as React.CSSProperties;

    const styleBottomInner = {
        '--bottomInner-min-height': (openedBottomLayout ? EXPANDED_HEIGHT_FOR_INNER : topPosition) + 'px',
        '--bottomInner-top': `${topPosition}px`,
    } as React.CSSProperties;

    return (
        <div
            className={styles.separateLayout}
            data-test={widget.uid}
            style={{
                ...getInlineStyles({
                    variables: {
                        expandedShadowColor: theme.palette?.expandedShadowColor ?? 'var(--black_48_tr_base)',
                    },
                }),
                backgroundColor: theme.palette?.backgroundColor,
            }}
        >
            <div
                className={clsx(styles.separateLayoutOverlay, (animationInProgress === 1 || openedBottomLayout) && styles.overlay)}
                onClick={handleClickOutside}
            />

            <div id="top-container">{topWidget}</div>
            <CoreLayoutView
                id="bottom-container"
                ref={ref}
                className={styles.separateLayoutBottom}
                style={styleBottom}
                styleName="separateLayoutSheet"
                parentStyleNames={parentStyleNames}
                styleProps={{ padding: { bottom: 96 } }}
                onScroll={onScroll}
                onTransitionEnd={() => {
                    setAnimationInProgress(0);
                    refStartAnimation.current = false;
                    // The sheet may be left open without a scroll: remember it as soon as it is expanded.
                    if (openedBottomLayout && restoreTargetRef.current === undefined) {
                        saveScroll();
                    }
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => {
                        isUserAction.current = false;
                    }, 100);
                }}
            >
                <div
                    className={classNamesBottomInner}
                    style={styleBottomInner}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchCancel}
                >
                    <Dash onTouchEnd={onDashTouch} styleName={'separateLayoutHandle'} parentStyleNames={parentStyleNames} />
                    <SeparateLayoutSheetContext value={sheet}>{bottomWidget}</SeparateLayoutSheetContext>
                </div>
            </CoreLayoutView>
        </div>
    );
};

export { SeparateContentLayout };
